import type { QueryRowType } from "@rocicorp/zero";
import { useQuery, useZero } from "@rocicorp/zero/react";
import { useEffect, useRef, useState, type ComponentProps } from "react";
import { sharedMutators } from "../../../shared/mutators";
import { queries } from "../../../shared/queries";
import {
  type GrantEntry,
  SCOPE_MANAGE_GRANTS_PERMISSION,
} from "../../../shared/rbac/roles";
import {
  useAffectedChatCount,
  useConnectorDisconnectImpact,
} from "../../hooks/useConnectorDisconnectImpact";
import { useGrantSpaceAccess } from "../../hooks/useGrantSpaceAccess";
import { useGroupMemberships } from "../../hooks/useGroupMemberships";
import { useHasAccess } from "../../hooks/useHasAccess";
import { useCurrentUserGrants } from "../../hooks/useCurrentUserGrants";
import { useCurrentUserId } from "../../hooks/useCurrentUserId";
import {
  connectorMembershipQueries,
  usedConnectorsForSpace,
} from "../../lib/connectorMembershipPreflight";
import { getWritableScopeIds } from "../../lib/getWritableScopeIds";
import { computeSpaceMemberSupplierGap } from "../../lib/spaceMemberSupplierGap";
import { deriveScopeGrantDelta } from "../../lib/spaceGrantDelta";
import { usedSkillsForSpace } from "../../lib/usedSuppliersForSpace";
import { grantEntriesToRows } from "../../lib/grantAssembly";
import { ConfirmModal } from "../ConfirmModal";
import { Count, DisconnectWarning } from "../DisconnectWarning";
import { ResourceAccessTable } from "./ResourceAccessTable";
import type { EditableScopeKind } from "./lib/inferScopeKind";

type Props = Pick<
  ComponentProps<typeof ResourceAccessTable>,
  | "principal"
  | "directGrants"
  | "inheritedGrants"
  | "resolveScopeName"
  | "personalSpaceId"
  | "canManage"
  | "stagedGrantsRef"
  | "onOpenResource"
  | "hideEmpty"
> & {
  active: boolean;
  grantsReady: boolean;
  customRoles: readonly QueryRowType<ReturnType<typeof queries.allRolesLite>>[];
};

type CommitScope = ComponentProps<typeof ResourceAccessTable>["commitScope"];

export function ScopeAccessEditor({ active, ...props }: Props) {
  const [visited, setVisited] = useState(active);
  if (active && !visited) setVisited(true);
  return visited ? <LoadedScopeAccessEditor {...props} /> : null;
}

function LoadedScopeAccessEditor({
  customRoles,
  grantsReady,
  ...props
}: Omit<Props, "active">) {
  const [allSpaces, spacesResult] = useQuery(
    queries.spaces({ settingsView: true })
  );
  const [apps, appsResult] = useQuery(queries.connectorsIndex());
  const [allSkills, skillsResult] = useQuery(queries.accessGraphSkills());
  const [preflightState, setPreflightState] = useState<
    "idle" | "pending" | "failed"
  >("idle");
  const mounted = useRef(false);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);
  const z = useZero();
  const currentUserId = useCurrentUserId();
  const isSelf = props.principal.userId === currentUserId;
  const { resolveScopeName } = props;
  const canReadSpaces = useHasAccess("tenant:spaces_read");
  const canReadApps = useHasAccess("tenant:connected_apps_read");
  const canReadSkills = useHasAccess("tenant:skills_read");
  const { userGroupIds: userGroupMemberships, loaded: membershipsLoaded } =
    useGroupMemberships();
  const [confirmingSelfRemoveAccess, setConfirmingSelfRemoveAccess] = useState<{
    scopeId: string;
    scopeKind: EditableScopeKind;
    desired: GrantEntry[];
  } | null>(null);
  const [pendingConnectorRemoval, setPendingConnectorRemoval] = useState<{
    appId: string;
    desired: GrantEntry[];
  } | null>(null);
  const [myGrants, myGrantsResult] = useCurrentUserGrants();
  const {
    guard: guardSpaceAccess,
    modal: grantAccessModal,
    loaded: sharersLoaded,
  } = useGrantSpaceAccess({
    ready:
      grantsReady &&
      membershipsLoaded &&
      [spacesResult, appsResult, skillsResult, myGrantsResult].every(
        (result) => result.type === "complete"
      ),
  });
  const resourcesLoaded =
    grantsReady &&
    sharersLoaded &&
    [spacesResult, appsResult, skillsResult, myGrantsResult].every(
      (result) => result.type === "complete"
    );
  const manageableSpaceIds = getWritableScopeIds(
    myGrants,
    allSpaces.map((s) => s.id),
    SCOPE_MANAGE_GRANTS_PERMISSION.space
  );
  const manageableAppIds = getWritableScopeIds(
    myGrants,
    apps.map((a) => a.id),
    SCOPE_MANAGE_GRANTS_PERMISSION.connected_app
  );
  const manageableSkillIds = getWritableScopeIds(
    myGrants,
    allSkills.map((s) => s.id),
    SCOPE_MANAGE_GRANTS_PERMISSION.skill
  );
  const scopesByKind = {
    space: allSpaces,
    connected_app: apps,
    skill: allSkills,
  } satisfies Record<EditableScopeKind, readonly { id: string }[]>;
  const currentGrantsFor = (scopeId: string, scopeKind: EditableScopeKind) =>
    scopesByKind[scopeKind].find((scope) => scope.id === scopeId)
      ?.roleGrantsAsScope;
  const isRemovalReady = (scopeKind: EditableScopeKind) =>
    grantsReady &&
    myGrantsResult.type === "complete" &&
    (scopeKind === "space"
      ? spacesResult
      : scopeKind === "connected_app"
        ? appsResult
        : skillsResult
    ).type === "complete";
  const commitScope: CommitScope = (
    scopeId,
    scopeKind,
    desired,
    onCommitted,
    onBlocked
  ) => {
    if (!isRemovalReady(scopeKind)) return false;
    const { add, remove } = deriveScopeGrantDelta(
      currentGrantsFor(scopeId, scopeKind) ?? [],
      desired
    );
    // Space grants also require access to the connectors and skills used there.
    const doMutate = () => {
      z.mutate(
        sharedMutators.patchScopeGrants({
          scopeId,
          scopeKind,
          add,
          remove,
          autoGrantConnectors: scopeKind === "space",
          autoGrantSkills: scopeKind === "space",
        })
      );
    };
    const space =
      scopeKind === "space"
        ? allSpaces.find((s) => s.id === scopeId)
        : undefined;
    if (!space || add.length === 0) {
      doMutate();
      return;
    }
    const commitSpace = async () => {
      setPreflightState("pending");
      try {
        const requests = connectorMembershipQueries(scopeId);
        const [usage, gitRemote] = await Promise.all([
          z.run(requests.commit_step, { type: "complete" }),
          z.run(requests.space_git_remote, { type: "complete" }),
        ]);
        if (!mounted.current) return false;
        const spaceGrants = space.roleGrantsAsScope;
        const gaps = computeSpaceMemberSupplierGap({
          currentSpaceGrants: spaceGrants,
          desiredSpaceGrants: grantEntriesToRows(desired, customRoles),
          connectors: usedConnectorsForSpace(apps, {
            commit_step: usage,
            space_git_remote: gitRemote,
          }),
          skills: usedSkillsForSpace(spaceGrants, allSkills),
          memberships: userGroupMemberships,
          manageableConnectorIds: manageableAppIds,
          manageableSkillIds,
        });
        setPreflightState("idle");
        if (gaps.length === 0) {
          doMutate();
          return;
        }
        guardSpaceAccess({
          spaceName: space.name,
          gaps,
          proceed: () => {
            doMutate();
            onCommitted?.();
          },
          onBlocked,
        });
      } catch {
        if (mounted.current) setPreflightState("failed");
      }
      return false;
    };
    return commitSpace();
  };
  const requestSelfRemoveAccess = (
    scopeId: string,
    scopeKind: EditableScopeKind,
    desired: GrantEntry[]
  ) => {
    setConfirmingSelfRemoveAccess({ scopeId, scopeKind, desired });
  };
  const removeConnectorAccess = (appId: string, desired: GrantEntry[]) => {
    setPendingConnectorRemoval({ appId, desired });
  };
  const [computeConnectorImpact, computeConnectorImpactLoaded] =
    useConnectorDisconnectImpact(pendingConnectorRemoval?.appId ?? null);
  const pendingConnectorApp = pendingConnectorRemoval
    ? apps.find((a) => a.id === pendingConnectorRemoval.appId)
    : null;
  const connectorRemovalImpact =
    pendingConnectorRemoval && pendingConnectorApp
      ? computeConnectorImpact(
          pendingConnectorApp.roleGrantsAsScope,
          pendingConnectorRemoval.desired
        )
      : null;
  const [connectorChatCount, connectorChatCountLoaded] = useAffectedChatCount(
    pendingConnectorRemoval?.appId ?? null,
    connectorRemovalImpact?.affectedUserIds ?? []
  );

  return (
    <>
      <ResourceAccessTable
        busy={preflightState === "pending"}
        selectionReady={resourcesLoaded && preflightState !== "failed"}
        isRemovalReady={isRemovalReady}
        {...props}
        spaces={allSpaces}
        skills={allSkills}
        apps={apps}
        customRoles={customRoles}
        isSelf={isSelf}
        manageableSpaceIds={manageableSpaceIds}
        manageableAppIds={manageableAppIds}
        manageableSkillIds={manageableSkillIds}
        hasTenantRead={{
          space: canReadSpaces,
          connected_app: canReadApps,
          skill: canReadSkills,
        }}
        commitScope={commitScope}
        onRequestSelfRemove={requestSelfRemoveAccess}
        onRemoveConnectorAccess={removeConnectorAccess}
      />
      {grantAccessModal}
      <ConfirmModal
        open={confirmingSelfRemoveAccess !== null}
        onClose={() => setConfirmingSelfRemoveAccess(null)}
        onConfirm={() => {
          if (confirmingSelfRemoveAccess) {
            commitScope(
              confirmingSelfRemoveAccess.scopeId,
              confirmingSelfRemoveAccess.scopeKind,
              confirmingSelfRemoveAccess.desired
            );
          }
          setConfirmingSelfRemoveAccess(null);
        }}
        confirmDisabled={
          !confirmingSelfRemoveAccess ||
          !isRemovalReady(confirmingSelfRemoveAccess.scopeKind)
        }
        title="Remove your own access?"
        description={
          confirmingSelfRemoveAccess
            ? `You’ll lose your direct access to ${
                resolveScopeName(
                  confirmingSelfRemoveAccess.scopeId,
                  confirmingSelfRemoveAccess.scopeKind
                ) ?? "this resource"
              }.`
            : ""
        }
        confirmLabel="Remove"
        destructive
      />

      <ConfirmModal
        open={pendingConnectorRemoval !== null}
        onClose={() => setPendingConnectorRemoval(null)}
        onConfirm={() => {
          if (
            !isRemovalReady("connected_app") ||
            !computeConnectorImpactLoaded ||
            !connectorChatCountLoaded
          )
            return;
          if (pendingConnectorRemoval)
            commitScope(
              pendingConnectorRemoval.appId,
              "connected_app",
              pendingConnectorRemoval.desired
            );
          setPendingConnectorRemoval(null);
        }}
        confirmDisabled={
          !isRemovalReady("connected_app") ||
          !computeConnectorImpactLoaded ||
          !connectorChatCountLoaded
        }
        title={isSelf ? "Remove your own access?" : "Remove access?"}
        description={
          connectorRemovalImpact &&
          connectorRemovalImpact.workflows.length > 0 ? (
            <>
              Removing this access means{" "}
              <Count
                n={connectorRemovalImpact.workflows.length}
                noun="workflow"
              />{" "}
              will need a connector replaced.
            </>
          ) : isSelf ? (
            `You’ll lose your direct access to ${pendingConnectorApp?.name ?? "this connector"}.`
          ) : (
            `Removes direct access to ${pendingConnectorApp?.name ?? "this connector"}.`
          )
        }
        confirmLabel="Remove"
        destructive
      >
        {connectorRemovalImpact && (
          <DisconnectWarning
            {...connectorRemovalImpact}
            chatCount={connectorChatCount}
          />
        )}
      </ConfirmModal>
    </>
  );
}
